import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Mail, Send, CheckCircle2, AlertTriangle, Users, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailPage = () => {
  const [config, setConfig] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);

  // Compose form
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [extraEmails, setExtraEmails] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [replyTo, setReplyTo] = useState('info@semplificaffitto.it');
  const [search, setSearch] = useState('');

  // Auth: cookie httpOnly automatico (axios.defaults.withCredentials=true in AuthContext)

  useEffect(() => {
    fetchConfig();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchConfig = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/email/config`);
      setConfig(data);
    } catch (e) { console.warn('fetchConfig:', e); toast.error('Errore caricamento config'); }
    finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users`);
      const list = Array.isArray(data) ? data : (data.users || []);
      setUsers(list.filter(u => u.email && !u.is_archived));
    } catch (e) { console.warn('fetchUsers:', e); }
  };

  const handleTest = async () => {
    if (!testTo) { toast.error('Inserisci destinatario test'); return; }
    setTesting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/email/test`, {
        to: testTo,
        subject: 'Test SMTP Semplificaffitto',
        body: 'Questa è una email di test inviata da Semplificaffitto.it'
      });
      toast.success(`✓ ${data.message}`);
    } catch (e) {
      toast.error(`Errore: ${e.response?.data?.detail || e.message}`);
    } finally { setTesting(false); }
  };

  const toggleUser = (id) => {
    setSelectedUserIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const filteredUsers = users.filter(u => {
    const q = search.toLowerCase();
    return !q || (u.nome || '').toLowerCase().includes(q)
      || (u.cognome || '').toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q)
      || (u.ragione_sociale || '').toLowerCase().includes(q);
  });

  const handleSend = async () => {
    const extraList = extraEmails.split(/[,;\s]+/).map(s => s.trim()).filter(s => /\S+@\S+\.\S+/.test(s));
    if (selectedUserIds.length === 0 && extraList.length === 0) {
      toast.error('Seleziona almeno un destinatario');
      return;
    }
    if (!subject.trim()) { toast.error('Inserisci oggetto'); return; }
    if (!bodyText.trim()) { toast.error('Inserisci messaggio'); return; }

    setSending(true);
    try {
      const bodyHtml = `<div style="font-family: Arial, sans-serif; color:#333; max-width:600px;">
<div style="white-space:pre-wrap;">${bodyText.replace(/</g, '&lt;')}</div>
<hr style="margin:24px 0; border:none; border-top:1px solid #ddd;"/>
<table cellpadding="0" cellspacing="0" style="font-size:12px; color:#666;">
<tr><td style="padding-right:12px;">
<strong style="color:#1e40af;">Semplificaffitto.it</strong><br/>
<em>by EDILSTUDIO s.r.l.</em>
</td><td>
Per assistenza: <a href="mailto:info@semplificaffitto.it">info@semplificaffitto.it</a>
</td></tr>
</table>
</div>`;

      const { data } = await axios.post(`${API_URL}/api/admin/email/send`, {
        to: extraList,
        user_ids: selectedUserIds,
        subject,
        body_text: bodyText,
        body_html: bodyHtml,
        reply_to: replyTo,
      });

      toast.success(`✓ Email inviata a ${data.recipients.length} destinatari`);
      // Reset form
      setSelectedUserIds([]);
      setExtraEmails('');
      setSubject('');
      setBodyText('');
    } catch (e) {
      toast.error(`Errore invio: ${e.response?.data?.detail || e.message}`);
    } finally { setSending(false); }
  };

  if (loading) return <div className="p-6 text-slate-500">Caricamento...</div>;

  return (
    <div className="p-6 space-y-6" data-testid="email-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Mail className="w-6 h-6 text-blue-600" /> Email
        </h1>
        <p className="text-sm text-slate-500">Invio email manuale tramite SMTP Aruba</p>
      </div>

      {/* CONFIG CARD */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            Configurazione SMTP
            {config?.password_set ? (
              <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1"/>Configurato</Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3 mr-1"/>Non configurato</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div><span className="text-slate-500">Server:</span> <strong>{config?.host}:{config?.port}</strong></div>
            <div><span className="text-slate-500">Mittente:</span> <strong>{config?.from_name} &lt;{config?.username}&gt;</strong></div>
            <div className="col-span-2"><span className="text-slate-500">BCC archivio:</span> <strong>{config?.bcc_archivio}</strong></div>
          </div>
          <div className="flex gap-2 pt-3 border-t">
            <Input
              placeholder="email@destinatario.it"
              value={testTo}
              onChange={e => setTestTo(e.target.value)}
              className="max-w-xs text-sm"
              data-testid="email-test-to"
            />
            <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} data-testid="email-test-btn">
              {testing ? 'Invio...' : 'Test invio'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* COMPOSE CARD */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Nuova email</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Selezione destinatari */}
          <div>
            <Label className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4" /> Destinatari ({selectedUserIds.length} selezionati)
            </Label>
            <Input
              placeholder="Cerca utente per nome, email, ragione sociale..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="mb-2"
              data-testid="email-search-users"
            />
            <div className="max-h-48 overflow-auto border rounded-lg p-2 bg-slate-50">
              {filteredUsers.length === 0 ? (
                <p className="text-xs text-slate-500 text-center py-4">Nessun utente trovato</p>
              ) : (
                <div className="space-y-1">
                  {filteredUsers.slice(0, 30).map(u => {
                    const selected = selectedUserIds.includes(u.id);
                    const display = u.is_societa ? (u.ragione_sociale || u.email)
                      : `${u.cognome || ''} ${u.nome || ''}`.trim() || u.email;
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => toggleUser(u.id)}
                        className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center justify-between
                          ${selected ? 'bg-blue-100 border border-blue-300' : 'bg-white hover:bg-slate-100'}`}
                        data-testid={`email-user-${u.id}`}
                      >
                        <span>
                          <strong>{display}</strong>
                          <span className="text-slate-500 ml-2 text-xs">{u.email}</span>
                        </span>
                        {selected && <X className="w-3.5 h-3.5 text-blue-600" />}
                      </button>
                    );
                  })}
                  {filteredUsers.length > 30 && (
                    <p className="text-xs text-slate-400 text-center pt-2">+ altri {filteredUsers.length - 30} - affina la ricerca</p>
                  )}
                </div>
              )}
            </div>
            {selectedUserIds.length > 0 && (
              <Button variant="ghost" size="sm" className="text-xs mt-2" onClick={() => setSelectedUserIds([])}>
                Deseleziona tutti
              </Button>
            )}
          </div>

          {/* Email aggiuntive */}
          <div>
            <Label className="mb-1 block">Altri destinatari (separati da virgola)</Label>
            <Input
              placeholder="indirizzo1@example.com, indirizzo2@example.com"
              value={extraEmails}
              onChange={e => setExtraEmails(e.target.value)}
              data-testid="email-extra-emails"
            />
          </div>

          <div>
            <Label className="mb-1 block">Reply-To (risposte arrivano qui)</Label>
            <Input value={replyTo} onChange={e => setReplyTo(e.target.value)} data-testid="email-reply-to" />
          </div>

          <div>
            <Label className="mb-1 block">Oggetto</Label>
            <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Oggetto della email..." data-testid="email-subject" />
          </div>

          <div>
            <Label className="mb-1 block">Messaggio</Label>
            <Textarea
              value={bodyText}
              onChange={e => setBodyText(e.target.value)}
              placeholder="Buongiorno,&#10;&#10;..."
              rows={10}
              data-testid="email-body"
            />
            <p className="text-xs text-slate-400 mt-1">
              La firma "Semplificaffitto.it by EDILSTUDIO s.r.l." viene aggiunta automaticamente.
            </p>
          </div>

          <div className="flex justify-between items-center pt-2 border-t">
            <p className="text-xs text-slate-500">
              {selectedUserIds.length + extraEmails.split(/[,;\s]+/).filter(s => /\S+@\S+\.\S+/.test(s)).length} destinatari + BCC archivio
            </p>
            <Button onClick={handleSend} disabled={sending} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="email-send-btn">
              <Send className="w-4 h-4 mr-2" /> {sending ? 'Invio in corso...' : 'Invia email'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailPage;
